const en = {
    nav: {
        manifesto: "About",
        services: "What I solve",
        process: "How it works",
        work: "Case studies",
        pricing: "Pricing",
        blog: "Field notes",
        contact: "Contact",
        bookCall: "Book a call",
    },
    hero: {
        overline: "Austin, Texas · Custom software developer · 20+ years building tools",
        headlineA: "I turn operational chaos into",
        headlineEm: "tools that pay for themselves.",
        headlineB: "",
        body:
            "Hi, I'm Guillermo. I've been obsessed with building tools since I was a kid in front of a Windows 95 tower — that obsession is what pushed me into this career and what keeps me in it, 20+ years later. I work with one client at a time, scope is real before a line of code is written, and the price is agreed up front.",
        ctaCall: "Book a discovery call",
        ctaWhatsapp: "WhatsApp me",
        stats: [
            { k: "20+", label: "Years building tools" },
            { k: "1–3 wk", label: "Average build window" },
            { k: "Clear", label: "No surprise invoices" },
            { k: "Monthly", label: "Care from day 1" },
        ],
    },
    manifesto: {
        overline: "A note from the studio",
        portraitAlt: "Guillermo Figueroa Mesa at WordCamp Europe, Porto",
        waterAlt: "Out on the water",
        caption: "Pictured: out on the water — the only place I let work wait.",
        headlineA: "Twenty-plus years of code,",
        headlineEm: "one stubborn habit:",
        headlineB: "solve the puzzle in front of me.",
        p1A: "For twenty-plus years I've built the software behind other people's businesses — the site that captures a lead while you're on stage, the tool that stops orders from getting lost, the admin panel that lets a non-technical owner update prices without paying a developer for a typo. I've done it for startups and small teams across the US, Europe and Latin America — including work on Google Assistant experiences at ",
        p1Jam: "Jam3",
        p1B: ".",
        p2A: "Most small businesses don't need an agency, a 12-week sprint, or a Notion full of Slack channels. They need ",
        p2Em: "one developer",
        p2B: " who can listen this week, scope the next, and pick up the phone when something needs a tweak.",
        p3: "That's what I do here, on my own time, outside of my agency — so the price stays honest, the relationship stays personal, and your product stays alive long after the launch screenshot.",
        p4:
            "I don't build software because it's trendy. I build it because solving the puzzle in front of me is the only thing that has held my attention for twenty-plus years. It's the reason I studied this career, and the reason I'm still in it.",
    },
    services: {
        overline: "What I solve",
        headlineA: "Two problems.",
        headlineEm: "One human",
        headlineB: "building the fix.",
        intro:
            "I keep the offer narrow on purpose. Every engagement is scoped and priced up front after a paid assessment, and every build comes with optional monthly care so your tool doesn't quietly rot six months later.",
        enquire: "Talk to me",
        tracks: [
            {
                kicker: "Track 01 · Customer-facing",
                title: "The website your business deserves — one that answers the phone when you can't.",
                desc: "Editorial custom sites that turn a visitor into a scheduled call, a booked event, or a paid order — while you're doing something else. Not templates. Not the same site your competitor already used.",
                deliverables: [
                    "A public site that captures leads while you sleep",
                    "Booking, WhatsApp, SMS or Calendly on the page",
                    "Ranked on Google without you having to babysit it",
                    "Edit typos and update pages yourself — no developer needed",
                ],
                cta: "From $3,780",
            },
            {
                kicker: "Track 02 · Back-of-house",
                title: "The tool that stops your business from running on WhatsApp and memory.",
                desc: "A private tool that takes the chaos out of your operation — orders, inventory, bookings, team handoffs. Built around how you already work, not how a subscription tool wishes you did.",
                deliverables: [
                    "A private tool tailored to your daily flow",
                    "Connects to your existing systems — payments, e-commerce, whatever you use",
                    "Replaces copy-paste, spreadsheets and manual updates",
                    "Each teammate sees only their part — no accidental deletes",
                ],
                cta: "From $6,650",
            },
        ],
    },
    process: {
        overline: "How it works",
        headlineA: "A short, honest",
        headlineEm: "path",
        headlineB: "from problem to live.",
        intro:
            "No discovery deck, no sprint zero, no \"let me sync with my PM\". Every project begins with a paid assessment so the scope is real before we write a single line of code.",
        stepLabel: "Step",
        steps: [
            {
                n: "01",
                title: "Assessment",
                body: "A paid 1–2 day audit (US$500) where I dive into your business, your stack and what you actually need built. You leave with a clear written read-out — yours to keep, even if we don't work together.",
            },
            {
                n: "02",
                title: "Proposal",
                body: "A single, short doc grounded in the assessment: scope, deliverables, one number, one timeline. No retainers buried in PDFs, no change-order surprises later.",
            },
            {
                n: "03",
                title: "Build",
                body: "I build it on your domain, share progress live, and ship to production. Average build window is 1–3 weeks. You don't pay anything more until you've seen it working.",
            },
            {
                n: "04",
                title: "Care",
                body: "Pick a monthly care plan (or don't). Hosting, content tweaks, bug fixes, small new features — handled by the same person who built the thing.",
            },
        ],
    },
    portfolio: {
        overline: "Case studies · 2025",
        headlineA: "Every project is the",
        headlineEm: "whole system",
        headlineB: "— the customer-facing surface and the private tool behind it.",
        liveBadge: "Live site",
        privateBadge: "Private · Internal",
        privateNote: "Private build · case study on request",
        visit: "Visit",
        readCaseStudy: "Read the case study",
        backToHome: "Back to all case studies",
        seeAll: "See all case studies",
        filters: {
            allLabel: "All",
            industryTitle: "Industry",
            productTypeTitle: "Product type",
            outcomeTitle: "Outcome",
            reset: "Reset filters",
            empty: "No case studies match those filters yet. Try clearing one.",
            resultsOne: "case study",
            resultsMany: "case studies",
            industries: {
                "music-arts": "Music & Arts",
                manufacturing: "Manufacturing",
                "saas-ops": "SaaS & Operations",
                hospitality: "Hospitality",
                "food-beverage": "Food & Beverage",
            },
            productTypes: {
                "public-site": "Public site",
                "internal-dashboard": "Internal dashboard",
                "internal-erp": "Internal ERP",
                chatbot: "Chatbot",
                automation: "Automation",
                ai: "AI-powered",
                analytics: "Analytics",
            },
            outcomes: {
                "capture-leads": "Capture more leads",
                "sell-more": "Sell more",
                "automate-ops": "Automate operations",
                "save-time": "Save hours weekly",
                "self-serve": "Own your content",
                "understand-users": "Understand customers",
            },
        },
        index: {
            metaTitle: "Case studies · Guillermo Figueroa Mesa | gfirem",
            metaDescription:
                "Every custom software project I've shipped for entrepreneurs and small teams — public sites, internal dashboards, ERPs and automations. Filter by industry, product type, or the outcome you're chasing.",
            overline: "Selected work · 2005 → today",
            headlineA: "Every tool I've shipped,",
            headlineEm: "with the problem it solved.",
            headlineB: "",
            intro:
                "Filter by industry, by the shape of the tool you might need, or by the business outcome you're chasing. Every entry links to a full case study with the problem, what I built and the outcome.",
        },
        related: {
            title: "Related case studies",
            subtitle: "Other work in the same neighborhood.",
        },
        sidebar: {
            client: "Client",
            category: "Category",
            location: "Location",
            liveSite: "Live site",
            access: "Access",
        },
        labels: {
            problem: "The problem",
            solution: "What I built",
            outcome: "The outcome",
        },
        bottomNote:
            "A handful of other projects live quietly inside companies — agencies, e-commerce stores, Web3 startups. Happy to share those on a call.",
        works: [
            {
                n: "01",
                client: "Terry Barber",
                location: "New Jersey · Performs worldwide",
                category: "Touring artist · Countertenor",
                outcomeKicker: "Site updates from a phone, mid-tour",
                title:
                    "A touring artist's site that ships new work faster than the tour bus can move.",
                intro:
                    "Self-edit admin from a phone, first-party analytics that flag improvements, and an on-site chat that catches bookings on the road.",
                problem:
                    "A touring countertenor needed to update his site every time a program, an album, or a tour date changed — and every update meant a ticket to a developer. Bookings and press inquiries came through a contact form nobody was watching between shows.",
                solution:
                    "A performance-first artist site with a full self-edit admin: programs, albums, press quotes, tour dates and FAQs — all editable from a phone. First-party analytics that surface improvement suggestions automatically, an on-site chat that reaches him wherever he is, and Lighthouse-green performance across mobile and desktop.",
                outcome:
                    "Terry ships new work the moment it exists. The admin tells him what's working (and what to fix) without him reading a chart. Bookings arrive through a live chat instead of dying in a contact form. Mobile visitors get the same fast, cinematic site as desktop.",
                meta: ["Self-edit admin", "First-party analytics", "Chat + booking"],
            },
            {
                n: "02",
                client: "DGE Cigars ERP",
                location: "Internal product · Santo Domingo ↔ US",
                category: "Manufacturing · Orders · Fulfillment",
                outcomeKicker: "Zero \"where is my order?\" emails",
                title: "An ERP that connects the storefront to the factory floor.",
                intro:
                    "One order timeline from WooCommerce checkout to the cigar factory in Santo Domingo to the US customer's mailbox — no more copy-pasting into WhatsApp.",
                problem:
                    "WooCommerce orders landed in one inbox, the cigar factory in Santo Domingo worked from a WhatsApp group, and shipping happened on a third tab. Every status update was being typed by hand into WhatsApp, and customers had no idea where their cigars were.",
                solution:
                    "A custom ERP that unifies storefront → factory → carrier. Single timeline per order (New → In Production → Ready to Ship → Shipped → Delivered), carrier tracking auto-attached, per-order fulfillment routing, and a status page the end customer can check.",
                outcome:
                    "The factory team in Santo Domingo updates a status once and it reaches the US customer instantly — no more copy-pasting into WhatsApp. \"Where is my order?\" emails have quietly stopped.",
                meta: ["WooCommerce sync", "Status timeline", "Carrier tracking"],
            },
            {
                n: "03",
                client: "Scalater Command Center",
                location: "Internal product · Austin",
                category: "Operations · DORA · AI",
                outcomeKicker: "300+ requests, one dashboard",
                title: "An internal command center for a software studio.",
                intro:
                    "300+ active requests, 30 reusable process templates, DORA + custom metrics and an embedded AI assistant — three modes for engineers, ops and clients.",
                problem:
                    "A growing software studio was running 300+ active requests across Slack DMs, emails and spreadsheets. Nobody knew which clients were engaged this week, which processes had been documented, or how the team actually spent their hours.",
                solution:
                    "An internal command center with custom + DORA metrics, 30 reusable process templates, page-view analytics per client portal, and an embedded AI assistant. Three modes: engineers, ops, and clients.",
                outcome:
                    "Engineers stop reinventing onboarding every time. Ops spots disengagement before a client ghosts. Clients see the same dashboard the team does, so trust compounds instead of leaking.",
                meta: ["Custom + DORA metrics", "AI assistant", "Client + team modes"],
            },
            {
                n: "04",
                client: "López Hill",
                location: "Spicewood, Texas",
                category: "Boutique wedding venue",
                outcomeKicker: "One inbox, more weddings closed",
                title:
                    "A public site, a lead chatbot and a private team portal.",
                intro:
                    "Editorial venue site, a 24/7 chatbot that pre-qualifies couples, and a mobile-first team portal so the López family answers fewer messages and books more weddings.",
                problem:
                    "A family-run hill-country venue was answering wedding inquiries across Instagram DMs, WhatsApp, phone calls they couldn't always pick up, and \"contact us\" emails. Leads slipped through the cracks every week. The family doesn't use spreadsheets — they use phones.",
                solution:
                    "An editorial public site, a tequila-tasting product page, a lead chatbot that qualifies couples 24/7 (even when the phone rings unanswered), and a private mobile-first dashboard where the López family confirms inquiries, tracks bookings and reviews chatbot conversations.",
                outcome:
                    "Every couple that visits the site lands in one queue the family actually checks. The chatbot pre-qualifies before the human steps in, so the family answers fewer messages and books more weddings.",
                meta: ["Editorial site", "Lead chatbot", "Team portal"],
            },
            {
                n: "05",
                client: "Island Flavors",
                location: "Austin, Texas",
                category: "Latin-inspired catering",
                outcomeKicker: "Menu updates in minutes, not days",
                title:
                    "A bilingual storefront, a catering menu and a self-edit admin.",
                intro:
                    "Bilingual landing, three-tier per-guest menus, a self-edit admin for dishes and a QR-code generator clients scan on event night.",
                problem:
                    "A home-based Latin cook was catering Austin's bilingual market from her kitchen — emailing PDFs of three-tier menus with per-guest pricing, fielding the same questions every week, and asking her developer every time a dish changed. At events, printed menus got lost or looked off-brand.",
                solution:
                    "A bilingual landing, three-tier dinner menus with per-guest pricing, a private admin where she self-edits dishes, and a QR-code generator clients scan on event night.",
                outcome:
                    "Menus update in minutes, not days. Bilingual guests read the menu in their language. She stops being a part-time webmaster — and her catered events feel like a brand, not a Google Doc.",
                meta: ["Bilingual site", "Catering menu", "QR + self-edit admin"],
            },
        ],
    },
    testimonials: {
        overline: "In their words",
        headlineA: "Quiet praise from",
        headlineEm: "the people I've actually shipped with.",
        items: [
            {
                body:
                    "Guille shipped our internal order tool in a week. Three years later it's still running, and he's still the person we text when something breaks.",
                author: "Founder · Foodhuggers",
                place: "USA",
            },
            {
                body:
                    "We needed someone who could think like a product person and code like a senior. We got both, in one human, with a clear price and no agency overhead.",
                author: "Head of Product · UserDao",
                place: "Web3 studio",
            },
            {
                body:
                    "He rebuilt our WooCommerce → BuddyPress flow in two days. What our previous developer estimated at six weeks.",
                author: "CTO · ThemeKraft",
                place: "Germany",
            },
        ],
    },
    pricing: {
        overline: "Honest pricing",
        headlineA: "Start with an assessment.",
        headlineEm: "Then a real number.",
        intro:
            "Every engagement begins with a paid assessment so the scope is real before we commit. From there I send a single proposal with a starting number. Care is optional, monthly, and cancellable any time.",
        afterLaunchOverline: "After launch",
        afterLaunchTitle: "Monthly care, by the same hands that built it.",
        afterLaunchBody:
            "Three light tiers, scoped on a call after launch. Cancel with seven days' notice — no retainers, no exit fees.",
        tiers: [
            {
                name: "Assessment",
                price: "$500",
                cadence: "starting from · 1–2 days · keeps the deliverable",
                for: "Anyone wanting an honest read of their problem, stack or business before committing",
                includes: [
                    "Discovery call + async questionnaire",
                    "Architecture, stack & cost review",
                    "Written scope + recommended next step",
                    "Yours to keep — even if we don't build together",
                    "Credited toward the build if you continue",
                ],
                cta: "Book the assessment",
            },
            {
                name: "Website",
                price: "from $3,780",
                cadence: "starting from · delivered in 1–3 weeks",
                for: "Restaurants, venues, studios, single-product stores",
                includes: [
                    "4–6 page editorial site, custom designed",
                    "Not a Squarespace or Wix template",
                    "Lead capture (form, WhatsApp, SMS, Calendly)",
                    "Ranked on Google — Business Profile set up",
                    "Optional edit-it-yourself panel",
                ],
                cta: "Start a site",
            },
            {
                name: "Internal Tool",
                price: "from $6,650",
                cadence: "starting from · monthly care possible",
                for: "Private tools for your team — dashboards, admin panels, order/inventory systems",
                includes: [
                    "A private tool tailored to how you already work",
                    "Connects to your existing systems (payments, e-commerce, etc.)",
                    "Each teammate sees only their part, with a change history",
                    "You see it working from day one, not just at the end",
                    "Optional monthly care from launch",
                ],
                cta: "Scope a tool",
            },
        ],
        care: [
            ["Care · Light", "Hosting, backups, security patches, up to 1h of small changes per month."],
            ["Care · Standard", "Everything in Light plus up to 4h of new features or content per month."],
            ["Care · Studio", "Priority Slack/WhatsApp channel plus up to 10h of work per month."],
        ],
    },
    faq: {
        overline: "Honest answers",
        headlineA: "Things people ask",
        headlineEm: "before the call.",
        intro:
            "If your question isn't here, message me on WhatsApp — I usually answer same day.",
        items: [
            {
                q: "Is this really one developer, or a hidden agency?",
                a: "Just me, Guillermo. I do run a separate software company (Scalater DEV LLC) for larger engagements, and gfirem is part of that — but this site exists specifically for direct, one-on-one work with small businesses and founders.",
            },
            {
                q: "Do you build MVPs?",
                a: "Yes — an MVP is just a first working version of a product: small enough to launch fast, real enough to sell. Whether you call it an MVP, a prototype, or a Version 1, that's the shape most of my custom-system projects take. If the word \"MVP\" isn't familiar, no problem — we'll talk about your problem, not the jargon.",
            },
            {
                q: "Why a paid assessment first?",
                a: "Because $500 of careful thinking up front saves both of us weeks of wrong assumptions later. You get a written read-out of your problem, stack and recommended next step — yours to keep, even if we don't build together. If you continue, the assessment fee is credited toward the build.",
            },
            {
                q: "How long do builds typically take?",
                a: "Most builds land between 1 and 3 weeks of active work after the assessment, depending on scope. Custom systems with multiple integrations can take longer — but you'll always have one number and one timeline before we begin.",
            },
            {
                q: "Do you write the copy and find the photos?",
                a: "I can do a first pass on copy and source stock or AI-generated imagery, especially for landings. If you have a brand or a photographer, even better — I love designing around real material.",
            },
            {
                q: "Can I cancel maintenance any time?",
                a: "Yes. Monthly care is month-to-month, cancellable with a 7-day notice. No 12-month retainers, no exit fees.",
            },
            {
                q: "Where are you based and which timezones do you work in?",
                a: "I'm based in Austin, Texas (US Central time) and work comfortably with US, LATAM and European clients. WhatsApp and email replies inside business hours, same day.",
            },
        ],
    },
    contact: {
        overline: "Begin the conversation",
        headlineA: "Tell me about",
        headlineEm: "your problem.",
        headlineB: "I'll tell you honestly if I can solve it.",
        intro:
            "Pick the channel you're fastest in. Discovery calls are free and there is no \"funnel\" on the other side — just me, with a notebook open.",
        ctaCall: "Book a discovery call",
        ctaWhatsapp: "Message on WhatsApp",
        rowWhatsapp: "WhatsApp",
        rowCalendly: "Calendly",
        rowEmail: "Email",
        rowLocation: "Based in",
        rowAvailability: "Availability",
        availabilityValue: "Accepting 3 small projects",
        localeTag: "en-US",
    },
    footer: {
        tagline:
            "An independent studio of one. Quietly building software for small businesses since well before it was a niche.",
        location: "Working with US, LATAM & EU clients.",
        partOf: "gfirem is part of Scalater DEV LLC",
        siteLabel: "Site",
        reachLabel: "Reach me",
        bookCall: "Book a discovery call",
        siteLinks: [
            ["#manifesto", "About"],
            ["#services", "What I solve"],
            ["#work", "Case studies"],
            ["#pricing", "Pricing"],
            ["#contact", "Contact"],
        ],
        copy: "A solo practice by Guillermo Figueroa Mesa · part of Scalater DEV LLC.",
        quote:
            "\u201CNo matter the language or the technology — solve the puzzle in front.\u201D",
    },
    meta: {
        title:
            "Custom Software Developer in Austin | Guillermo Figueroa",
    },
    notFound: {
        code: "404",
        overline: "Wrong door",
        title: "This page took a walk out to the water.",
        body:
            "Whatever you were looking for isn't here — maybe the link got old, maybe I moved it, maybe it never existed. Nothing broke; you just landed on a page that isn't part of the studio.",
        ctaHome: "Back to the home page",
        ctaWork: "Read the case studies",
        ctaContact: "Message me instead",
    },
    blog: {
        metaTitle: "Field notes · Custom software for small businesses | gfirem",
        metaDescription:
            "Honest guides, comparisons and lessons on hiring a solo developer for a small-business tool — from a builder with 20+ years in the field.",
        overline: "Field notes",
        title: "Honest guides for small-business owners weighing custom software.",
        lede: "Comparisons, buying guides and lessons from 20+ years of shipping tools that pay for themselves.",
        empty: "First article coming soon — check back this week.",
        fallbackNotice: "Notes coming soon in Spanish · showing the original English version",
        backToIndex: "All field notes",
        writtenBy: "Written by",
        moreNotes: "Read more field notes →",
    },
    toggle: {
        en: "EN",
        es: "ES",
        aria: "Switch language",
    },
};

export default en;
en;
